import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { EditorialFacebookPostResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

export interface FormDataT {
  message: string;
  link: string;
  owner: string;
  channel_id: number;
  published_at?: string;
  site_visit_benchmark: number;
  reach_duration: number;
}

interface Props {
  onClose: () => void;
  post: EditorialFacebookPostResource;
}

const EditForm: FC<Props> = ({ onClose, post }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      message: post.message,
      link: post.link,
      owner: post.owner,
      published_at: post.publishedAt,
      site_visit_benchmark: post.siteVisitBenchmark,
      reach_duration: post.reachDuration,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        const body = {
          message: data.message,
          link: data.link,
          owner: data.owner,
          ...(!isNotEditable && {
            site_visit_benchmark: data.site_visit_benchmark,
            reach_duration: data.reach_duration,
          }),
          ...(!isNotEditable &&
            !post.queueStatus && {
              published_at: data.published_at,
            }),
        };

        await api.put(`/v1/editorial/facebook-posts/${post.id}`, body);

        await queryClient.invalidateQueries(
          ['editorial_distribution_schedules'],
          {
            exact: false,
          },
        );

        onClose();

        alerts.success('Post has been successfully updated');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  const isNotEditable =
    DateTime.fromISO(post.publishedAt) <= DateTime.now().plus({ minutes: 10 });

  return (
    <Modal>
      <SidePanel heading={`Edit Post`} onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                label="Message"
                type="textarea"
                control={control}
                name="message"
              />
              <FormField<FormDataT>
                label="Article Link"
                control={control}
                name="link"
                inputType="url"
              />
              <FormField<FormDataT>
                label="Owner"
                control={control}
                name="owner"
              />
              {!post.queueStatus && (
                <FormField<FormDataT>
                  label="Date & Time"
                  control={control}
                  name={`published_at`}
                  type="datepicker"
                  disabled={isNotEditable}
                />
              )}
              <FormField<FormDataT>
                label="Site Visit Benchmark"
                control={control}
                placeholder="Add site visit benchmark"
                name={`site_visit_benchmark`}
                disabled={isNotEditable}
              />
              <FormField<FormDataT>
                label="Reach Duration (min)"
                control={control}
                placeholder="Add reach duration"
                name={`reach_duration`}
                disabled={isNotEditable}
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Update
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default EditForm;
